'use client';

import React from 'react';
import { twMerge } from 'tailwind-merge';

type TypographyVariant =
  | 'h1'
  | 'h2'
  | 'h3'
  | 'h4'
  | 'h5'
  | 'h6'
  | 'subtitle1'
  | 'subtitle2'
  | 'body1'
  | 'body2'
  | 'caption'
  | 'button'
  | 'overline';

interface TypographyProps {
  children: React.ReactNode;
  variant?: TypographyVariant;
  component?: React.ElementType;
  align?: 'left' | 'center' | 'right' | 'justify';
  color?: string;
  gutterBottom?: boolean;
  noWrap?: boolean;
  paragraph?: boolean;
  className?: string;
  style?: React.CSSProperties;
}

export const Typography: React.FC<TypographyProps> = ({
  children,
  variant = 'body1',
  component,
  align = 'left',
  color,
  gutterBottom = false,
  noWrap = false,
  paragraph = false,
  className = '',
  style = {},
  ...otherProps
}) => {
  // Determine which HTML element to use
  const Component =
    component ||
    (variant === 'h1'
      ? 'h1'
      : variant === 'h2'
        ? 'h2'
        : variant === 'h3'
          ? 'h3'
          : variant === 'h4'
            ? 'h4'
            : variant === 'h5'
              ? 'h5'
              : variant === 'h6'
                ? 'h6'
                : paragraph
                  ? 'p'
                  : 'span');

  // Define variant classes
  const variantClasses = {
    h1: 'text-4xl font-bold leading-tight',
    h2: 'text-3xl font-bold leading-tight',
    h3: 'text-2xl font-bold leading-tight',
    h4: 'text-xl font-bold leading-tight',
    h5: 'text-lg font-bold leading-tight',
    h6: 'text-base font-bold leading-tight',
    subtitle1: 'text-base font-bold leading-normal',
    subtitle2: 'text-sm font-bold leading-normal',
    body1: 'text-base font-normal leading-normal',
    body2: 'text-sm font-normal leading-relaxed',
    caption: 'text-xs font-normal leading-relaxed',
    button: 'text-sm font-medium leading-loose uppercase',
    overline: 'text-xs font-normal leading-loose uppercase',
  }[variant];

  // Define alignment classes
  const alignClasses = {
    left: 'text-left',
    center: 'text-center',
    right: 'text-right',
    justify: 'text-justify',
  }[align];

  // Define color class
  const colorClass = color ? `text-${color}` : '';

  // Define margin classes
  const marginClasses = gutterBottom ? 'mb-1.5' : '';

  // Define text overflow classes
  const textOverflowClasses = noWrap ? 'whitespace-nowrap overflow-hidden text-ellipsis' : '';

  // Combine all classes
  const classes = twMerge(
    'm-0 p-0 mt-0',
    variantClasses,
    alignClasses,
    marginClasses,
    textOverflowClasses,
    className
  );

  return (
    <Component
      className={classes}
      style={color && !colorClass ? { color } : undefined}
      {...otherProps}
    >
      {children}
    </Component>
  );
};

export default Typography;
